import { useTranslation } from 'react-i18next';

import { useMemo, useState } from 'react';

import { Combobox, InputBase, useCombobox } from '@optiro/ui';

import { useCreateLocation } from '@/hooks/useCreateLocation/useCreateLocation';
import { useLocations } from '@/hooks/useLocations/useLocations';
import { Location } from '@/types/types';

const CREATE_OPTION_VALUE = '$create';

export interface SelectLocationProps {
  projectId: number;
  value: Location['id'] | null;
  onChange: (value: Location['id'] | null) => void;
  clearable?: boolean;
  disabled?: boolean;
}

const CLEAR_OPTION_VALUE = '$clear';

export const SelectLocation = ({
  projectId,
  value,
  onChange,
  clearable,
  disabled,
}: SelectLocationProps) => {
  const { t } = useTranslation('task');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });
  const { data: locations = [] } = useLocations(projectId, { enabled: !disabled });
  const createLocation = useCreateLocation(projectId);
  const [search, setSearch] = useState('');

  const selected = useMemo(() => locations.find((l) => l.id === value) ?? null, [locations, value]);

  const trimmedQuery = search.trim();

  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return locations;
    return locations.filter((l) => l.name.toLowerCase().includes(q));
  }, [locations, trimmedQuery]);

  const hasExactMatch = useMemo(
    () => locations.some((l) => l.name.toLowerCase() === trimmedQuery.toLowerCase()),
    [locations, trimmedQuery]
  );

  const showCreateOption = trimmedQuery.length > 0 && !hasExactMatch;

  const handleSelect = async (submittedValue: string) => {
    if (submittedValue === CREATE_OPTION_VALUE) {
      const created = await createLocation.mutateAsync({ name: trimmedQuery });
      onChange(created.id);
      combobox.closeDropdown();
      return;
    }
    if (submittedValue === CLEAR_OPTION_VALUE) {
      onChange(null);
      setSearch('');
      combobox.closeDropdown();
      return;
    }
    onChange(Number(submittedValue) as Location['id']);
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleSelect}>
      <Combobox.Target>
        <InputBase
          component="input"
          type="text"
          role="textbox"
          disabled={disabled}
          value={combobox.dropdownOpened ? search : (selected?.name ?? '')}
          placeholder={t('location.placeholder')}
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {clearable && !trimmedQuery && (
            <Combobox.Option value={CLEAR_OPTION_VALUE} c="dimmed">
              {t('location.none')}
            </Combobox.Option>
          )}
          {filtered.map((l) => (
            <Combobox.Option value={String(l.id)} key={l.id}>
              {l.name}
            </Combobox.Option>
          ))}
          {showCreateOption && (
            <Combobox.Option value={CREATE_OPTION_VALUE}>
              {t('location.create', { name: trimmedQuery })}
            </Combobox.Option>
          )}
          {filtered.length === 0 && !showCreateOption && (
            <Combobox.Empty>{t('location.empty')}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default SelectLocation;
